import React, { useEffect, useState } from 'react';

import { toProjectDateOnly } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

import type { DraftItem } from '../types.local';
import RelativeWeightModal from './RelativeWeightModal';
import { toNumberOrNull } from 'src/utils/formatter';

export default function WbsDetailsPanel({
    item,
    status,
    hasPublished,
    isDeleted,
    onChange,
    onAddSibling,
    onAddChild,
    onSaveSingleItem,
    onSaveContent,
    onRemove,
    onSoftDelete,
    onUndoDelete,
    getFamilyOf,
}: {
    item: DraftItem | null;
    status: 'ADDED' | 'MODIFIED' | 'DELETED' | null;
    hasPublished: boolean;
    isDeleted: boolean;
    onChange: (nodeId: string, patch: Partial<DraftItem>) => void;
    onAddSibling: () => void;
    onAddChild: () => void;
    onSaveSingleItem: (nodeId: string, paryload: Partial<DraftItem>) => Promise<void>;
    onSaveContent: () => void;
    onRemove: (item: DraftItem, op: 'discard' | 'retire' | 'auto') => void;
    onSoftDelete: (item: DraftItem) => void;
    onUndoDelete: (item: DraftItem) => void;
    getFamilyOf: (nodeId: string) => { parent: DraftItem | null; siblings: DraftItem[] };
}) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const [name, setName] = useState('');
    const [memo, setMemo] = useState<string | null>(null);
    const [plannedStart, setPlannedStart] = useState<string | null>(null);
    const [plannedFinish, setPlannedFinish] = useState<string | null>(null);
    const [weight, setWeight] = useState<number | null>(null);

    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState<number | null>(null);

    const [openRelativeWeightModal, setOpenRelativeWeightModal] = useState(false);
    const [family, setFamily] = useState<{ parent: DraftItem | null; siblings: DraftItem[] }>({
        parent: null,
        siblings: [],
    });

    // 모달 오픈시 초기화 로직
    const onOpenRelativeWeightModal = () => {
        if (!item?.nodeId) return;
        const fam = getFamilyOf(item.nodeId);

        setFamily(fam);
        setOpenRelativeWeightModal(true);
    };

    // 저장 완료 배지 자동 숨김
    useEffect(() => {
        if (!savedAt) return;
        const t = setTimeout(() => setSavedAt(null), 2000);
        return () => clearTimeout(t);
    }, [savedAt, setSavedAt]);

    useEffect(() => {
        setName(item?.name ?? '');
        setMemo(item?.memo ?? null);

        // ✅ 신규(ADDED & 미발행)이고 plannedStart/Finish가 비어있으면,
        // ✅ 부모의 plannedStart/Finish를 "표시 기본값"으로만 사용
        const isNewLocal = status === 'ADDED' && !hasPublished;

        let fallbackStart = '';
        let fallbackFinish = '';

        if (item?.nodeId) {
            const fam = getFamilyOf(item.nodeId);
            const p = fam?.parent ?? null;
            if (p?.plannedStart) fallbackStart = toProjectDateOnly(p.plannedStart, zone);
            if (p?.plannedFinish) fallbackFinish = toProjectDateOnly(p.plannedFinish, zone);
        }

        setPlannedStart(
            item?.plannedStart ? toProjectDateOnly(item.plannedStart, zone) : isNewLocal ? fallbackStart : '',
        );
        setPlannedFinish(
            item?.plannedFinish ? toProjectDateOnly(item.plannedFinish, zone) : isNewLocal ? fallbackFinish : '',
        );

        setWeight(toNumberOrNull(item?.weight ?? null));
    }, [
        item?.memo,
        item?.name,
        item?.nodeId,
        item?.plannedFinish,
        item?.plannedStart,
        item?.weight,
        zone,
        status,
        hasPublished,
        getFamilyOf,
    ]);

    if (!item) return <div className="text-sm text-gray-500">좌측에서 항목을 선택하세요.</div>;

    const isNew = status === 'ADDED' && !hasPublished;
    const isModified = status === 'MODIFIED' && !isDeleted;
    const isSoftDeleted = !!isDeleted;

    const primaryLabel = isModified ? '수정' : '저장';

    // 공통 버튼 클래스
    const btn = 'px-3 py-1 text-sm rounded border transition-colors hover:bg-gray-50 active:scale-[0.99]';
    const btnDanger =
        'px-3 py-1 text-sm rounded border border-red-400 text-red-600 transition-colors hover:bg-red-50 active:scale-[0.99]';
    const btnPrimary =
        'px-3 py-1 text-sm rounded border border-blue-500 text-blue-600 transition-colors hover:bg-blue-50 active:scale-[0.99] disabled:opacity-60';

    return (
        <div className="flex flex-col gap-3 min-w-0 p-2 md:p-3">
            {/* 메타 정보: flex-wrap로 1~3칸 자동 배치 */}
            <div className="flex flex-wrap gap-2 text-xs md:text-sm text-gray-500">
                <div className="min-w-[220px] flex-1">
                    ID: <span className="font-mono">{item.nodeId ?? (item as any).cid ?? '—'}</span>
                </div>
                <div className="min-w-[220px] flex-1">
                    Code/Level: <span className="font-mono">{item.code ?? '—'}</span> /{' '}
                    <span className="font-mono">{item.level ?? '—'}</span>
                </div>
                <div className="min-w-[220px] flex-1">
                    Parent: <span className="font-mono">{item.parentNodeId ?? 'null'}</span>
                </div>
            </div>

            {/* 이름 */}
            <label className="text-sm min-w-0 block">
                <div className="mb-1 text-gray-600">Name</div>
                <input
                    className="border rounded px-2 py-1 w-full min-w-0"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name"
                />
            </label>

            {/* 날짜: flex-wrap (컨테이너 폭이 충분하면 2칸, 좁으면 자동 1칸) */}
            <div className="flex flex-wrap gap-2">
                <label className="text-sm flex-1 min-w-[240px]">
                    <div className="mb-1 text-gray-600">Planned Start</div>
                    <input
                        className="border rounded px-2 py-1 w-full min-w-0"
                        type="date"
                        value={plannedStart ?? ''}
                        onChange={(e) => setPlannedStart(e.target.value || null)}
                    />
                </label>
                <label className="text-sm flex-1 min-w-[240px]">
                    <div className="mb-1 text-gray-600">Planned Finish</div>
                    <input
                        className="border rounded px-2 py-1 w-full min-w-0"
                        type="date"
                        value={plannedFinish ?? ''}
                        onChange={(e) => setPlannedFinish(e.target.value || null)}
                    />
                </label>
            </div>

            {/* 가중치 / 담당자: flex-wrap (컨테이너 폭이 충분하면 2칸, 좁으면 자동 1칸) */}
            <div className="flex flex-wrap gap-2">
                <label className="text-sm flex-1 min-w-[240px]">
                    <div className="mb-1 text-gray-600 flex items-center gap-2">
                        <span>Weight</span>
                        {/* 트리거 버튼: 기존 onOpenRelativeWeightModal를 그대로 사용 */}
                        <button
                            type="button"
                            className="px-2 py-0.5 text-xs rounded border hover:bg-gray-50"
                            onClick={onOpenRelativeWeightModal}
                            title="부모 기준 상대(%)로 형제들의 절대 가중치 계산"
                        >
                            형제별 %배분
                        </button>
                    </div>
                    <input
                        className="border rounded px-2 py-1 w-full min-w-0"
                        type="number"
                        step="0.0001"
                        min="0"
                        value={weight ?? ''}
                        onChange={(e) => setWeight(e.target.value === '' ? null : Number(e.target.value))}
                    />
                </label>

                <label className="text-sm flex-1 min-w-[240px]">
                    <div className="mb-1 text-gray-600">Assignee</div>
                    <input
                        className="border rounded px-2 py-1 w-full min-w-0"
                        disabled
                        value=""
                        onChange={() => {}}
                        placeholder="회사를 선택할 수 있도록 수정"
                    />
                </label>
            </div>

            {/* 메모 */}
            <label className="text-sm min-w-0 block">
                <div className="mb-1 text-gray-600">Memo</div>
                <textarea
                    className="border rounded px-2 py-1 w-full min-w-0 h-24 md:h-32 resize-y"
                    value={memo ?? ''}
                    onChange={(e) => setMemo(e.target.value || null)}
                />
            </label>

            {/* 하단 액션: 항상 좌우 justify-between */}
            <div className="flex flex-wrap items-center justify-between gap-2 w-full">
                {/* 왼쪽 액션 그룹 */}
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <button className={btn} onClick={onAddSibling}>
                        같은 레벨에 추가
                    </button>
                    <button className={btn} onClick={onAddChild}>
                        하위 레벨에 추가
                    </button>

                    {/* 상태별 삭제/취소 버튼 */}
                    {isNew && (
                        <button className={btnDanger} onClick={() => onRemove(item, 'discard')}>
                            Draft 삭제
                        </button>
                    )}
                    {isModified && (
                        <button className={btnDanger} onClick={() => onRemove(item, 'discard')}>
                            수정 취소
                        </button>
                    )}
                    {isSoftDeleted && (
                        <button className={btn} onClick={() => onUndoDelete(item)}>
                            삭제 취소
                        </button>
                    )}
                    {!isSoftDeleted && !isNew && (
                        <button className={btnDanger} onClick={() => onSoftDelete(item)}>
                            WBS 삭제(소프트)
                        </button>
                    )}
                </div>

                {/* 오른쪽 저장 버튼 */}
                <button
                    className={btnPrimary}
                    disabled={saving || !item?.nodeId}
                    onClick={async () => {
                        if (!item?.nodeId) return;
                        try {
                            setSaving(true);
                            await onSaveSingleItem(item.nodeId, {
                                name,
                                memo,
                                plannedStart: plannedStart || null,
                                plannedFinish: plannedFinish || null,
                                weight,
                            });
                            setSavedAt(Date.now());
                        } finally {
                            setSaving(false);
                        }
                    }}
                >
                    {saving ? `${primaryLabel} 중...` : primaryLabel}
                </button>
            </div>

            {!!savedAt && (
                <div className="text-xs text-gray-500">마지막 저장: {new Date(savedAt).toLocaleString()}</div>
            )}

            {/* 모달 마운트 */}
            <RelativeWeightModal
                open={openRelativeWeightModal}
                onClose={() => setOpenRelativeWeightModal(false)}
                parent={family.parent}
                siblings={family.siblings}
                precision={4}
                onChange={onChange}
                onSaveSingleItem={onSaveSingleItem}
            />
        </div>
    );
}
